import { useOverviewMetrics, useCostMetrics } from '../hooks/useMetrics';
import type { FunnelStage, ActivityEvent } from '../types';

const PAGE_STYLE = {
  padding: '32px 36px',
  maxWidth: '1200px',
};

const CARD_STYLE = {
  backgroundColor: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  padding: '20px 22px',
};

function MetricCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: number | string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div
      style={{
        ...CARD_STYLE,
        borderTop: `3px solid ${accent ?? '#e5e7eb'}`,
      }}
    >
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
        {label}
      </div>
      <div style={{ fontSize: '32px', fontWeight: 800, color: '#111827', lineHeight: 1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>{sub}</div>
      )}
    </div>
  );
}

function FunnelViz({ stages }: { stages: FunnelStage[] }) {
  const max = Math.max(...stages.map((s) => s.count), 1);

  return (
    <div style={{ ...CARD_STYLE, marginTop: '24px' }}>
      <h3 style={{ margin: '0 0 20px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
        Pipeline Funnel
      </h3>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0', overflowX: 'auto' }}>
        {stages.map((stage, i) => {
          const widthPct = Math.max(15, (stage.count / max) * 100);
          const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];
          const color = colors[i % colors.length];
          const isLast = i === stages.length - 1;

          return (
            <div key={stage.label} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', whiteSpace: 'nowrap' }}>
                  {stage.label}
                </div>
                <div
                  style={{
                    height: '36px',
                    backgroundColor: color,
                    borderRadius: i === 0 ? '6px 0 0 6px' : isLast ? '0 6px 6px 0' : '0',
                    opacity: 0.15 + (widthPct / 100) * 0.85,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
                    {stage.count.toLocaleString()}
                  </span>
                </div>
              </div>
              {!isLast && (
                <div style={{ color: '#9ca3af', fontSize: '16px', margin: '0 2px', paddingTop: '18px' }}>›</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityFeed({ events }: { events: ActivityEvent[] }) {
  const typeIcon: Record<string, string> = {
    DISCOVERED: '🔍',
    QUALIFIED: '✅',
    DRAFT_READY: '📝',
    SENT: '📤',
    REPLIED: '↩',
    HIGH_INTENT: '🔥',
    REJECTED: '✗',
    SUPPRESSED: '⊘',
  };

  return (
    <div style={{ ...CARD_STYLE, flex: 1 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
        Recent Activity
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
        {events.length === 0 && (
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>No recent activity.</p>
        )}
        {events.map((evt) => (
          <div
            key={evt.id}
            style={{
              display: 'flex',
              gap: '12px',
              padding: '10px 0',
              borderBottom: '1px solid #f3f4f6',
              alignItems: 'flex-start',
            }}
          >
            <span style={{ fontSize: '16px', width: '22px', textAlign: 'center' }}>
              {typeIcon[evt.type] ?? '•'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                {evt.companyName}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{evt.description}</div>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
              {new Date(evt.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CostSummary({ costThisWeek }: { costThisWeek: number }) {
  return (
    <div
      style={{
        ...CARD_STYLE,
        width: '240px',
        minWidth: '200px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <h3 style={{ margin: '0', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
        Cost This Week
      </h3>
      <div style={{ fontSize: '28px', fontWeight: 800, color: '#111827' }}>
        ${costThisWeek.toFixed(2)}
      </div>
      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: 'auto' }}>
        AI inference &amp; data enrichment
      </div>
    </div>
  );
}

export function Overview() {
  const { data: metrics, isLoading, isError } = useOverviewMetrics();
  const { data: costs } = useCostMetrics();

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div style={PAGE_STYLE}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: 800, color: '#111827' }}>
            Pipeline Overview
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: '#9ca3af' }}>{today}</p>
        </div>
        {isLoading && (
          <span style={{ fontSize: '13px', color: '#9ca3af', marginTop: '6px' }}>Refreshing…</span>
        )}
      </div>

      {isError && (
        <div style={{ padding: '16px', backgroundColor: '#fee2e2', borderRadius: '8px', color: '#991b1b', marginBottom: '24px', fontSize: '14px' }}>
          Failed to load metrics. Retrying…
        </div>
      )}

      {/* Metric Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        <MetricCard label="Discovered Today" value={metrics?.discoveredToday ?? '—'} accent="#3b82f6" />
        <MetricCard label="Filtered" value={metrics?.filteredToday ?? '—'} sub="not matching ICP" accent="#6b7280" />
        <MetricCard label="Qualified" value={metrics?.qualifiedToday ?? '—'} accent="#8b5cf6" />
        <MetricCard
          label="Awaiting Approval"
          value={metrics?.awaitingApproval ?? '—'}
          sub="need your review"
          accent="#f59e0b"
        />
        <MetricCard label="Sent This Week" value={metrics?.sentThisWeek ?? '—'} accent="#10b981" />
        <MetricCard
          label="Reply Rate"
          value={metrics ? `${metrics.replyRate.toFixed(1)}%` : '—'}
          accent="#06b6d4"
        />
        <MetricCard label="Positive Replies" value={metrics?.positiveReplies ?? '—'} accent="#22c55e" />
        <MetricCard label="High Intent" value={metrics?.highIntent ?? '—'} sub="ready for handoff" accent="#ef4444" />
      </div>

      {/* Funnel */}
      {metrics?.funnelStages && metrics.funnelStages.length > 0 && (
        <FunnelViz stages={metrics.funnelStages} />
      )}

      {/* Activity + Cost row */}
      <div style={{ display: 'flex', gap: '20px', marginTop: '24px' }}>
        <ActivityFeed events={metrics?.recentActivity ?? []} />
        <CostSummary costThisWeek={costs?.totalCostThisWeek ?? 0} />
      </div>
    </div>
  );
}
